import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, Check } from "lucide-react";
import api, { formatApiError } from "../lib/api";

const inputCls = "w-full border border-slate-300 bg-white px-3 py-2 text-sm focus:border-ink focus:outline-none";


const EMPTY_PROJECT = {
    name: "", developer: "", city: "", location: "", country: "India", market: "Noida", category: "Residential", project_type: "", property_type: "",
    starting_price: "Price on Request", configuration: "", possession: "", rera: "",
    short_description: "", overview: "", hero_image: "",
    highlights: "", configurations: "", pricing: "", amenities: "",
    gallery: "", location_info: "", investment_considerations: "", developer_info: "", published: true,
};

const LIST_FIELDS = ["highlights", "configurations", "pricing", "amenities", "gallery", "investment_considerations"];

const toForm = (p) => {
    const f = { ...EMPTY_PROJECT, ...p };
    LIST_FIELDS.forEach((k) => (f[k] = Array.isArray(p[k]) ? p[k].join("\n") : ""));
    return f;
};

const toPayload = (f) => {
    const p = { ...f };
    LIST_FIELDS.forEach((k) => (p[k] = String(f[k]).split("\n").map((s) => s.trim()).filter(Boolean)));
    return p;
};

const ProjectForm = ({ initial, onSave, onCancel, saving }) => {
    const [form, setForm] = useState(initial);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const textFields = [
        ["name", "Project Name *"], ["developer", "Developer"], ["city", "City"], ["location", "Location"],
        ["market_display", null], ["project_type", "Project Type"], ["property_type", "Property Type"],
        ["starting_price", "Starting Price"], ["configuration", "Configuration"], ["possession", "Possession"],
        ["rera", "RERA / Registration (where applicable)"], ["status", "Project Status"], ["hero_image", "Hero Image URL"],
    ].filter(([, label]) => label !== null);
    const areaFields = [
        ["short_description", "Short Description"], ["overview", "Overview"],
        ["highlights", "Key Highlights (one per line)"], ["configurations", "Configurations (one per line)"],
        ["pricing", "Pricing (one per line)"], ["amenities", "Amenities (one per line)"],
        ["gallery", "Gallery Image URLs (one per line)"], ["location_info", "Location Information"],
        ["investment_considerations", "Investment Considerations (one per line)"],
        ["developer_info", "Developer Information"],
    ];

    return (
        <form
            data-testid="project-form"
            onSubmit={(e) => { e.preventDefault(); onSave(toPayload(form)); }}
            className="mt-6 grid gap-4 border border-slate-200 bg-white p-6 sm:grid-cols-2"
        >
            {textFields.map(([k, label]) => (
                <div key={k}>
                    <label className="text-xs font-medium text-slate-600">{label}</label>
                    <input data-testid={`project-field-${k}`} className={`${inputCls} mt-1`} value={form[k]} onChange={set(k)} required={k === "name"} />
                </div>
            ))}
            <div>
                <label className="text-xs font-medium text-slate-600">Country *</label>
                <select data-testid="project-field-country" className={`${inputCls} mt-1`} value={form.country} onChange={set("country")}>
                    <option>India</option>
                    <option>Dubai</option>
                    <option>Other</option>
                </select>
            </div>
            <div>
                <label className="text-xs font-medium text-slate-600">Category *</label>
                <select data-testid="project-field-category" className={`${inputCls} mt-1`} value={form.category} onChange={set("category")}>
                    <option>Residential</option>
                    <option>Commercial</option>
                </select>
            </div>
            <div>
                <label className="text-xs font-medium text-slate-600">Market</label>
                <select data-testid="project-field-market" className={`${inputCls} mt-1`} value={form.market} onChange={set("market")}>
                    <option>Noida</option>
                    <option>Greater Noida</option>
                    <option>Gurugram</option>
                    <option>Dubai</option>
                    <option>Other</option>
                </select>
            </div>
            <div className="flex items-end gap-2 pb-1">
                <input id="pub" data-testid="project-field-published" type="checkbox" checked={form.published} onChange={set("published")} className="h-4 w-4" />
                <label htmlFor="pub" className="text-xs font-medium text-slate-600">Published (visible on website)</label>
            </div>
            {areaFields.map(([k, label]) => (
                <div key={k} className="sm:col-span-2">
                    <label className="text-xs font-medium text-slate-600">{label}</label>
                    <textarea data-testid={`project-field-${k}`} rows={k === "overview" ? 3 : 2} className={`${inputCls} mt-1 resize-y`} value={form[k]} onChange={set(k)} />
                </div>
            ))}
            <div className="flex gap-3 sm:col-span-2">
                <button data-testid="project-form-save" type="submit" disabled={saving} className="bg-ink px-6 py-2.5 text-[11px] font-medium uppercase tracking-[0.2em] text-ivory disabled:opacity-50">
                    {saving ? "Saving…" : "Save Project"}
                </button>
                <button data-testid="project-form-cancel" type="button" onClick={onCancel} className="border border-slate-300 px-6 py-2.5 text-[11px] font-medium uppercase tracking-[0.2em] text-slate-600">
                    Cancel
                </button>
            </div>
        </form>
    );
};

const Enquiries = ({ items, reload }) => (
    <div className="mt-6 space-y-4" data-testid="admin-enquiries-list">
        {items.length === 0 && <p className="text-sm text-slate-500">No enquiries yet.</p>}
        {items.map((en) => (
            <div key={en.id} className={`border bg-white p-5 ${en.read ? "border-slate-200" : "border-gold/60"}`} data-testid={`enquiry-${en.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <span className={`px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.15em] ${en.type === "buyer" ? "bg-ink text-ivory" : en.type === "partner" ? "bg-gold/20 text-ink" : "bg-slate-100 text-slate-600"}`}>
                            {en.type}
                        </span>
                        <p className="text-sm font-medium text-ink">{en.data.name}</p>
                        {!en.read && <span className="text-[10px] uppercase tracking-widest text-gold">New</span>}
                    </div>
                    <div className="flex items-center gap-2">
                        {!en.read && (
                            <button data-testid={`enquiry-read-${en.id}`} onClick={async () => { await api.patch(`/enquiries/${en.id}`); reload(); }} className="flex items-center gap-1 border border-slate-300 px-3 py-1.5 text-xs text-slate-600 hover:border-ink">
                                <Check className="h-3 w-3" /> Mark read
                            </button>
                        )}
                        <button data-testid={`enquiry-delete-${en.id}`} onClick={async () => { if (window.confirm("Delete this enquiry?")) { await api.delete(`/enquiries/${en.id}`); reload(); } }} className="flex items-center gap-1 border border-red-200 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50">
                            <Trash2 className="h-3 w-3" /> Delete
                        </button>
                    </div>
                </div>
                <dl className="mt-4 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-3">
                    {Object.entries(en.data).map(([k, v]) =>
                        v ? (
                            <div key={k}>
                                <dt className="text-[10px] uppercase tracking-widest text-slate-400">{k.replace(/_/g, " ")}</dt>
                                <dd className="font-light text-ink/80">{String(v)}</dd>
                            </div>
                        ) : null
                    )}
                </dl>
                <p className="mt-3 text-[10px] text-slate-400">{en.created_at ? new Date(en.created_at).toLocaleString() : ""}</p>
            </div>
        ))}
    </div>
);

const Projects = ({ items, reload }) => {
    const [editing, setEditing] = useState(null); // null | "new" | project object
    const [saving, setSaving] = useState(false);

    const save = async (payload) => {
        setSaving(true);
        try {
            if (editing === "new") await api.post("/projects", payload);
            else await api.put(`/projects/${editing.id}`, payload);
            toast.success("Project saved");
            setEditing(null);
            reload();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.name}"?`)) return;
        await api.delete(`/projects/${p.id}`);
        toast.success("Project deleted");
        reload();
    };

    return (
        <div data-testid="admin-projects-panel">
            {!editing && (
                <button data-testid="new-project-button" onClick={() => setEditing("new")} className="mt-6 flex items-center gap-2 bg-ink px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.2em] text-ivory">
                    <Plus className="h-4 w-4" /> New Project
                </button>
            )}
            {editing && (
                <ProjectForm
                    initial={editing === "new" ? EMPTY_PROJECT : toForm(editing)}
                    onSave={save}
                    onCancel={() => setEditing(null)}
                    saving={saving}
                />
            )}
            <div className="mt-6 divide-y divide-slate-100 border border-slate-200 bg-white" data-testid="admin-projects-list">
                {items.map((p) => (
                    <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`admin-project-${p.slug}`}>
                        <div className="flex items-center gap-4">
                            {p.hero_image && <img src={p.hero_image} alt="" className="h-12 w-16 object-cover" />}
                            <div>
                                <p className="text-sm font-medium text-ink">{p.name}</p>
                                <p className="text-xs text-slate-500">{p.category} &bull; {p.market} &bull; {p.location}</p>
                            </div>
                            {!p.published && <span className="bg-slate-100 px-2 py-0.5 text-[10px] uppercase tracking-widest text-slate-500">Draft</span>}
                        </div>
                        <div className="flex gap-2">
                            <button data-testid={`edit-project-${p.slug}`} onClick={() => setEditing(p)} className="flex items-center gap-1 border border-slate-300 px-3 py-1.5 text-xs text-slate-600 hover:border-ink">
                                <Pencil className="h-3 w-3" /> Edit
                            </button>
                            <button data-testid={`delete-project-${p.slug}`} onClick={() => remove(p)} className="flex items-center gap-1 border border-red-200 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50">
                                <Trash2 className="h-3 w-3" /> Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const ChangePassword = () => {
    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/auth/change-password", { current_password: current, new_password: next });
            toast.success("Password updated");
            setOpen(false);
            setCurrent("");
            setNext("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="change-password">
            <button data-testid="change-password-toggle" onClick={() => setOpen((o) => !o)} className="text-xs text-slate-500 hover:text-ink">
                Change password
            </button>
            {open && (
                <form onSubmit={submit} className="absolute right-6 top-14 z-10 w-72 space-y-3 border border-slate-200 bg-white p-4 shadow-md" data-testid="change-password-form">
                    <input type="password" required placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} className={inputCls} data-testid="cp-current" />
                    <input type="password" required placeholder="New password (10+ chars, letters & numbers)" value={next} onChange={(e) => setNext(e.target.value)} className={inputCls} data-testid="cp-new" />
                    <button type="submit" disabled={busy} className="w-full bg-ink py-2 text-[11px] font-medium uppercase tracking-[0.2em] text-ivory disabled:opacity-50" data-testid="cp-submit">
                        {busy ? "Updating…" : "Update Password"}
                    </button>
                </form>
            )}
        </div>
    );
};

const Admin = () => {
    const [user, setUser] = useState(null);
    const [tab, setTab] = useState("enquiries");
    const [enquiries, setEnquiries] = useState([]);
    const [projects, setProjects] = useState([]);
    const navigate = useNavigate();

    const loadEnquiries = () => api.get("/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    const loadProjects = () => api.get("/projects/all").then((r) => setProjects(r.data)).catch(() => {});

    useEffect(() => {
        let m = document.querySelector('meta[name="robots"]');
        if (!m) {
            m = document.createElement("meta");
            m.setAttribute("name", "robots");
            document.head.appendChild(m);
        }
        m.setAttribute("content", "noindex,nofollow");
        if (!localStorage.getItem("luxati_token")) {
            navigate("/admin/login");
            return;
        }
        api.get("/auth/me")
            .then((r) => {
                setUser(r.data);
                loadEnquiries();
                loadProjects();
            })
            .catch(() => {
                localStorage.removeItem("luxati_token");
                navigate("/admin/login");
            });
    }, [navigate]);

    if (!user)
        return (
            <main className="flex min-h-screen items-center justify-center bg-[#F1F5F9]" data-testid="admin-loading">
                <p className="text-sm text-slate-500">Loading…</p>
            </main>
        );

    const unread = enquiries.filter((e) => !e.read).length;

    return (
        <main data-testid="admin-dashboard" className="min-h-screen bg-[#F1F5F9]">
            <header className="border-b border-slate-200 bg-white">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
                    <div className="flex items-baseline gap-3">
                        <span className="font-serif text-xl font-medium tracking-[0.22em] text-ink">LUXATI</span>
                        <span className="text-[10px] uppercase tracking-[0.3em] text-slate-400">Admin</span>
                    </div>
                    <ChangePassword />
                    <button
                        data-testid="admin-logout"
                        onClick={() => { localStorage.removeItem("luxati_token"); navigate("/admin/login"); }}
                        className="flex items-center gap-2 text-xs text-slate-500 hover:text-ink"
                    >
                        <LogOut className="h-4 w-4" /> Sign out
                    </button>
                </div>
            </header>
            <div className="mx-auto max-w-6xl px-6 py-10">
                <div className="flex gap-3" data-testid="admin-tabs">
                    <button
                        data-testid="admin-tab-enquiries"
                        onClick={() => setTab("enquiries")}
                        className={`border px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.2em] ${tab === "enquiries" ? "border-ink bg-ink text-ivory" : "border-slate-300 bg-white text-slate-600"}`}
                    >
                        Enquiries {unread > 0 && <span className="ml-1 text-gold">({unread} new)</span>}
                    </button>
                    <button
                        data-testid="admin-tab-projects"
                        onClick={() => setTab("projects")}
                        className={`border px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.2em] ${tab === "projects" ? "border-ink bg-ink text-ivory" : "border-slate-300 bg-white text-slate-600"}`}
                    >
                        Projects ({projects.length})
                    </button>
                </div>
                {tab === "enquiries" && <Enquiries items={enquiries} reload={loadEnquiries} />}
                {tab === "projects" && <Projects items={projects} reload={loadProjects} />}
            </div>
        </main>
    );
};

export default Admin;
